import {makeAutoObservable, runInAction} from "mobx";
import {getStore, useStore} from "../index";
import axios from "axios";
import {md5OfFile, updateImagePreview} from "@/utils";


/**
 * @typedef {object} BannerSearchMap
 * @property {string} searchTitle
 * @property {number} offset
 * @property {number} limit
 */

/**
 * 메인페이지 비주얼 영역 배너 관련 상태 관리 Store
 */
export class BannerStore {

    /**
     * 공통 검색 관련 Store
     * @type {SearchBaseStore}
     */
    searchBaseStore;

    /**
     * 배너 등록 및 수정 데이터
     * @type {Object}
     */
    bannerSettingData = {};


    /**
     * 업로드된 이미지 파일 리스트
     * @type {Array<File|Object|string>}
     */
    imageFile = [];

    /**
     * 배너 검색 파라미터
     * @type {BannerSearchMap}
     */
    bannerSearchMap= {
        searchTitle: "",
        offset: 0,
        limit: 5,
    }

    /**
     * 노출 중인 배너 리스트
     * @type {Array<Object>}
     */
    visibleBanner = [];


    /**
     * 비노출 배너 리스트
     * @type {Array<Object>}
     */
    hiddenBanner = [];

    /**
     * 비노출 배너 총 개수
     * @type {number}
     */
    hiddenBannerCount = 0;

    /**
     * 기존 노출 배너 ID 리스트
     * 순서 변경 여부 비교용
     * @type {Array<string>}
     */
    visibleBannerOriginalIds = [];

    /**
     * 노출 배너 순서 변경 여부
     * @type {boolean}
     */
    visibleOrderDirty = false;

    /**
     * 이미지 미리보기 데이터 맵
     * @type {Object}
     */
    imagePreviewMap = {};

    constructor(store) {
        this.store = store;
        this.searchBaseStore = store.searchBaseStore;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /**
     * 배너 리스트 조회 함수
     *
     * @param {string|undefined} params URL Query String
     * @returns {Promise<void>}
     */
    async setFetchBannerList(params = undefined) {
        let finalParams = "";
        if(params){
            const search = new URLSearchParams(params.startsWith("?") ? params.slice(1) : params);
            finalParams = `?${search.toString()}`;
        }
        const response = await axios.get(`/api/banner${finalParams}`);
        if(response) {
            runInAction(() => {
                this.visibleBanner = response.data.data.displayBanners;
                this.hiddenBanner = response.data.data.banners;
                this.hiddenBannerCount = response.data.data.count;
                this.visibleBannerOriginalIds = this.visibleBanner.map(item => item._id);
                this.visibleOrderDirty = false;
            });
        }
    }

    /**
     * 배너 삭제 함수
     *
     * @param {string} id 배너 ID
     * @returns {Promise<AxiosResponse>}
     */
    async setDeleteBanner(id) {
        const payload = {
            bannerIds: [id],
        };
        return axios.delete("/api/banner", {
            data: payload,
        });
    }

    /**
     * 배너 노출 순서 및 상태 변경 함수
     * visible / hidden / reOrder 타입 처리
     *
     * @param {string} type 변경 타입
     * @returns {Promise<AxiosResponse>}
     */
    async setRefreshOrder(type) {
        let payload = {};
        if (type === "visible"){
            const baseDisplayBanners = this.visibleBanner.map(item => ({
                _id: item._id,
                display: item.display,
            }));
            const selectedItems = this.setSelectBannerCheck("hiddenBannerRow", this.hiddenBanner);
            const ids = selectedItems.map(item => item?._id);
            const startDisplay = baseDisplayBanners.length ? baseDisplayBanners[baseDisplayBanners.length - 1].display + 1 : 1;
            const displayBannerMap = ids.map((id, index) => ({
                _id: id,
                display: startDisplay + index,
            }));
            payload = {
                displayBanners: [...baseDisplayBanners, ...displayBannerMap],
            };
        } else if(type === "hidden"){
            const selectedItems = this.setSelectBannerCheck("visibleBannerRow", this.visibleBanner);
            const ids = selectedItems.map(item => item?._id);

            const remainVisible = this.visibleBanner.filter(item => !ids.includes(item._id));

            const displayBanners = remainVisible.map((item, index) => ({
                _id: item._id,
                display: index + 1,
            }));

            payload = { displayBanners };
        } else if(type === "reOrder"){
            const displayBanners = this.visibleBanner.map((item, index) => ({
                _id: item._id,
                display: index + 1,
            }));
            payload = { displayBanners };
        }
        return await axios.patch("/api/banner/displayOrder", payload);
    }

    /**
     * 노출 배너 순서 변경 함수
     *
     * @param {number} fromIndex 기존 인덱스
     * @param {number} toIndex 이동할 인덱스
     */
    reorderVisible(fromIndex, toIndex) {
        if (fromIndex === toIndex) return;
        const next = Array.from(this.visibleBanner);
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        this.visibleBanner = next;
        this.checkVisibleOrderDirty();
    }


    /**
     * 노출 배너 순서 변경 여부 체크 함수
     */
    checkVisibleOrderDirty() {
        const currentIds = this.visibleBanner.map(item => item._id);
        const orig = this.visibleBannerOriginalIds;
        if (currentIds.length !== orig.length) {
            this.visibleOrderDirty = true;
            return;
        }
        this.visibleOrderDirty = currentIds.some((id, index) => id !== orig[index]);
    }


    /**
     * 체크된 배너 데이터 반환 함수
     *
     * @param {string} name 체크박스 그룹명
     * @param {Array} list 배너 리스트
     * @returns {Array|undefined}
     */
    setSelectBannerCheck(name, list) {
        if(list && list.length > 0){
            if (!this.searchBaseStore.checkedMap[name] || this.searchBaseStore.checkedMap[name].length === 0) return;
            return this.searchBaseStore.checkedMap[name].map(index => list[index]);
        }
    }

    /**
     * 배너 리스트 및 검색 상태 초기화 함수
     */
    setInitBanner() {
        this.visibleBanner = [];
        this.hiddenBanner = [];
        this.hiddenBannerCount = 0
        this.visibleBannerOriginalIds = [];
        this.visibleOrderDirty = false;
        this.bannerSearchMap= {
            searchTitle: "",
            offset: 0,
            limit: 5,
        }
    }


    /**
     * 배너 상세 데이터 조회 및 수정 데이터 세팅 함수
     *
     * @param {string} id 배너 ID
     * @returns {Promise<void>}
     */
    async setModifyBannerData(id){
        const response = await axios.get(`/api/banner/detail?bannerId=${id}`);

        if(response){
            const bannerData = response.data.data?.banner;
            if(bannerData.title){
                this.setBannerSettingData("title", bannerData.title);
            }
            if(bannerData.updatedAt){
                this.setBannerSettingData("date", bannerData.title);
            }
            if(bannerData.data.image){
                const imageInfo = typeof bannerData.data.image === "string" ? JSON.parse(bannerData.data.image) : bannerData.data.image
                this.imageFile = [imageInfo];
                this.imagePreviewMap = updateImagePreview(this.imageFile);
            }
            if(bannerData.data.url){
                this.setBannerSettingData("url", bannerData.data.url);
            }
        }
    }

    /**
     * 배너 등록 및 수정 데이터 세팅 함수
     *
     * @param {string} type 데이터 타입
     * @param {*} value 저장할 값
     */
    setBannerSettingData(type, value) {
        this.bannerSettingData[type] = value
    }

    /**
     * 이미지 파일 및 미리보기 데이터 세팅 함수
     *
     * @param {Array<File>} files 업로드 파일 리스트
     * @returns {Promise<void>}
     */
    async setImageFilesData(files = []) {
        this.imageFile = files;
        this.imagePreviewMap = updateImagePreview(files);
    }

    /**
     * 업로드 이미지 삭제 함수
     *
     * @param {number} index 삭제할 파일 인덱스
     * @returns {Promise<void>}
     */
    async setRemoveImageFile(index) {
        const next = [...this.imageFile];
        next.splice(index, 1);
        this.imageFile = next;
        this.imagePreviewMap = updateImagePreview(next);
    }

    /**
     * 배너 등록 및 수정 저장 함수
     * 신규 파일 업로드 및 MD5 생성 포함
     *
     * @param {string|undefined} id 수정 시 배너 ID
     * @returns {Promise<AxiosResponse>}
     */
    async setBannerRegister(id = undefined) {
        const formData = new FormData();
        let fileInfos = [];
        const existingInfos = [];
        const newFiles = [];
        for (const file of this.imageFile) {
            if(file){
                if(typeof file === "string"){
                    if(JSON.parse(file).md5){
                        existingInfos.push(file);
                    } else {
                        newFiles.push(file);
                    }
                } else {
                    if(file.md5){
                        existingInfos.push(file);
                    } else {
                        newFiles.push(file);
                    }
                }
            }
        }
        for (const newFile of newFiles) {
            formData.append("file", newFile);
        }
        if (newFiles.length > 0) {
            const md5List = await Promise.all(newFiles.map(nf => md5OfFile(nf)));
            const newInfos = md5List.map((hash, i) => ({
                md5: hash,
                name: newFiles[i].name,
            }));
            fileInfos = existingInfos.concat(newInfos);
        } else {
            fileInfos = existingInfos;
        }
        const imageInfo = typeof fileInfos[0] === "string" ? JSON.parse(fileInfos[0]) : fileInfos[0]
        formData.append("image", JSON.stringify(imageInfo));
        formData.append("title", this.bannerSettingData["title"]);
        formData.append("url", this.bannerSettingData["url"]);
        if(id){
            formData.append("bannerId", id);
        }
        return await axios.post("/api/banner", formData);
    }

    /**
     * 배너 등록 데이터 초기화 함수
     */
    setInitBannerData() {
        this.bannerSettingData = {};
        this.imageFile = [];
        this.imagePreviewMap = {};
    }

    /**
     * 공통 검색 파라미터 초기화 함수
     *
     * @param {Object} param 초기화할 파라미터
     */
    clearParams(param) {
        this.searchBaseStore.searchParams = param;
    }
}

export const useBannerStore = () => useStore().bannerStore;
export const getBannerStore = () => getStore().bannerStore;
